import { useMemo, useState, useEffect } from "react";
import moment from "moment-timezone";
import { Skeleton } from "@/components/ui/skeleton";
import { Commodities } from "@/lib/api/commodity";
import { useListRiskSummaryFull, useListRiskSummaryUnknown } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level, get_commodity_color, get_commodity_border_color } from "./common";
import { ResponsivePie } from "@nivo/pie";
import { useQueryParam } from "@/hooks/use-query-param";
import { Button } from "@/components/ui/button";
import { formatNumberToDisplay } from "@/lib/api/common";
import { Edit, EditIcon, HistoryIcon, Eye, EyeOff, Table2, LayoutGrid } from "lucide-react";
import { RiskProfileHistoryModal } from "./RiskProfileHistoryModal";
import { EditCropProfileModal } from "./EditCropProfileModal";
import { CustomerNotesSection } from "./CustomerNotesSection";
import { CustomerTodosSection } from "./CustomerTodosSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn, CommonDestinationTimezone } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { CustomerProfileCards } from "./CustomerProfileCards";
import { CustomerProfileTable } from "./CustomerProfileTable";
import { CustomerBreakdownView } from "./MarketZoneBreakthroughs/CustomerBreakdownView";
import { RiskRevenueSummary } from "./RiskRevenue/Summary";
import { useListRecommendations, RecommendationRule, RecommendationGroup } from "@/lib/api/risk/recommendation";
import { CommodityOptions } from "@/lib/api/commodity";

// Helper function to find the matching rule based on hedged percent
function findMatchingRule(rules: RecommendationRule[], hedgedPercent: number): RecommendationRule | null {
    if (!rules || rules.length === 0) return null;

    for (const rule of rules) {
        const { range_type, range_constraint_value } = rule;

        if (range_type === "between") {
            const [min, max] = range_constraint_value.split("-").map(v => parseFloat(v.trim()));
            if (hedgedPercent >= min && hedgedPercent <= max) {
                return rule;
            }
        } else if (range_type === "<") {
            const value = parseFloat(range_constraint_value);
            if (hedgedPercent < value) {
                return rule;
            }
        } else if (range_type === "<=") {
            const value = parseFloat(range_constraint_value);
            if (hedgedPercent <= value) {
                return rule;
            }
        } else if (range_type === ">") {
            const value = parseFloat(range_constraint_value);
            if (hedgedPercent > value) {
                return rule;
            }
        } else if (range_type === ">=") {
            const value = parseFloat(range_constraint_value);
            if (hedgedPercent >= value) {
                return rule;
            }
        }
    }

    return null;
}

// Helper function to get commodity label from CommodityOptions
function getCommodityLabel(commodityType: string): string {
    const option = CommodityOptions.find(opt => opt.value === commodityType);
    return option?.label || commodityType;
}

function RecommendationSummary({ baseTopLevelResult, customerId }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>; customerId: string }) {
    const [cropYear] = useQueryParam("crop_year", "");
    const currentCropYear = parseInt(cropYear || moment.tz(CommonDestinationTimezone).format("YYYY"));

    // Fetch all recommendations for today (for caching)
    const todayDate = moment().format("YYYY-MM-DD");
    const recommendationsResult = useListRecommendations({ date: todayDate });

    const summaryData = baseTopLevelResult?.data?.data?.summary || {};

    const { isReady, commodity_top_level_items } = useMemo(() => {
        if (!summaryData?.commodity_list) return { isReady: false, commodity_top_level_items: [] };
        if (!summaryData?.customer_name_map) return { isReady: false, commodity_top_level_items: [] };

        const commodity_top_level_items = summaryData.commodity_list
            .map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData }))
            .filter(Boolean)
            .sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

        return { isReady: true, commodity_top_level_items };
    }, [summaryData, customerId]);

    const recommendationItems = useMemo(() => {
        if (!isReady || !recommendationsResult.data?.data) return [];

        const recommendations = recommendationsResult.data.data;
        const items = [];

        for (const commodity of commodity_top_level_items) {
            // Find matching recommendation for this commodity and crop year
            const matchingRecommendation = recommendations.find(
                (rec: RecommendationGroup) =>
                    rec.commodity_type === commodity.commodity_type &&
                    rec.crop_year === currentCropYear
            );

            if (matchingRecommendation) {
                const hedgedPercent = commodity.percent_hedged || 0;
                const matchingRule = findMatchingRule(matchingRecommendation.RecommendationRules, hedgedPercent);

                items.push({
                    commodityType: commodity.commodity_type,
                    commodityName: getCommodityLabel(commodity.commodity_type),
                    cropYear: currentCropYear,
                    hedgedPercent: hedgedPercent,
                    message: matchingRule?.message || "No recommendation available",
                    hasMatch: !!matchingRule,
                });
            }
        }

        return items;
    }, [isReady, recommendationsResult.data, commodity_top_level_items, currentCropYear]);

    if (!isReady || recommendationsResult.isLoading) {
        return null;
    }

    if (recommendationItems.length === 0) {
        return null;
    }

    return (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm">
            {recommendationItems.map((item, index) => (
                <div key={`${item.commodityType}-${item.cropYear}`}>
                    <span className="text-gray-400"> • </span>
                    <span className="font-semibold text-primary">{item.cropYear} {item.commodityName}</span>
                    <span className="text-gray-600"> - </span>
                    <span className={cn(item.hasMatch ? "text-blue-900" : "text-gray-600")}>
                        {item.message}
                    </span>
                </div>
            ))}
        </div>
    );
}

export function CustomerView({ baseTopLevelResult, customerId }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string }) {
    const [cropYear] = useQueryParam("crop_year", "");
    const [showTodos, setShowTodos] = useState(false);
    const currentCropYear = parseInt(cropYear || moment.tz(CommonDestinationTimezone).format("YYYY"))!;

    return (
        <div className="flex flex-col gap-4">
            <RecommendationSummary baseTopLevelResult={baseTopLevelResult} customerId={customerId}/>
            <CustomerProfileCardView baseTopLevelResult={baseTopLevelResult} customerId={customerId} />
            <div className={cn(showTodos ? "flex flex-col gap-4" : "flex flex-col md:grid md:grid-cols-3 gap-4")}>
                <Card className={cn(showTodos ? "": "col-span-2")}>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">Customer Notes & Activities</CardTitle>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowTodos(!showTodos)}
                                className="h-8"
                            >
                                {showTodos ? (
                                    <>
                                        <EyeOff className="h-4 w-4 mr-1" />
                                        Hide To-Dos
                                    </>
                                ) : (
                                    <>
                                        <Eye className="h-4 w-4 mr-1" />
                                        Show To-Dos
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className={cn('space-y-6', showTodos ? "md:grid md:grid-cols-2 gap-4" : "")}>
                            <CustomerNotesSection customerId={customerId} useAccordion={true} />
                            {showTodos && (
                                <CustomerTodosSection customerId={customerId} />
                            )}
                        </div>
                    </CardContent>
                </Card>

                <div className={cn(showTodos ? "flex flex-col gap-2 col-span-1" : "flex flex-col gap-2")}>
                    <RiskRevenueSummary baseTopLevelResult={baseTopLevelResult} />
                </div>

                <div className="col-span-3">
                    <CustomerBreakdownView customer_id={customerId} crop_year={currentCropYear} baseTopLevelResult={baseTopLevelResult} />
                </div>

                <div className={cn("flex flex-col gap-2 col-span-3")}>
                    <CustomerUnknownCropYearView baseTopLevelResult={baseTopLevelResult} customerId={customerId} />
                    <CustomerUnknownCurrentCropYearView baseTopLevelResult={baseTopLevelResult} customerId={customerId} />
                </div>
            </div>
        </div>
    );
}

export function CustomerProfileCardView({ baseTopLevelResult, customerId }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string }) {
    const [_commodityDisplayName, setSelectedCommodityDisplayName] = useQueryParam("commodity", "");
    const [cropYear] = useQueryParam("crop_year", "");
    const [selectedHistoryModal, setSelectedHistoryModal] = useState<{ commodityType: string } | null>(null);
    const [selectedEditModal, setSelectedEditModal] = useState<{ commodityType: string } | null>(null);
    const [isTableView, setIsTableView] = useState(false);
    const summaryData = baseTopLevelResult?.data?.data?.summary || {};

    const { isReady, customerName, commodity_top_level_items } = useMemo(() => {
        if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
        if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

        const customerName = customerId ? summaryData.customer_name_map[customerId] || "Unknown Customer" : "";

        const commodity_top_level_items = summaryData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

        return { isReady: true, customerName, commodity_top_level_items };
    }, [summaryData]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">{isReady ? "Crop Profiles" : (
                    <Skeleton className="h-4 w-[80px]" />
                )}</h2>
                {/* {isReady && (
                    <div className="flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                    <Switch
                    checked={isTableView}
                    onCheckedChange={setIsTableView}
                    aria-label="Toggle view"
                    />
                    <Table2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                    )} */}
            </div>
            {!isReady && (
                <div className="flex flex-col space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                </div>
            )}
            {isReady && !isTableView && (
                <CustomerProfileCards
                    commodity_top_level_items={commodity_top_level_items}
                    onEditClick={(commodityType) => setSelectedEditModal({ commodityType })}
                    onHistoryClick={(commodityType) => setSelectedHistoryModal({ commodityType })}
                    onCommodityClick={setSelectedCommodityDisplayName}
                />
            )}
            {isReady && isTableView && (
                <CustomerProfileTable
                    commodity_top_level_items={commodity_top_level_items}
                    customerId={customerId}
                    cropYear={parseInt(cropYear) || new Date().getFullYear()}
                    onEditClick={(commodityType) => setSelectedEditModal({ commodityType })}
                    onHistoryClick={(commodityType) => setSelectedHistoryModal({ commodityType })}
                    onCommodityClick={setSelectedCommodityDisplayName}
                />
            )}

            {selectedHistoryModal && (
                <RiskProfileHistoryModal
                    isOpen={true}
                    onClose={() => setSelectedHistoryModal(null)}
                    customerId={customerId}
                    commodityType={selectedHistoryModal.commodityType}
                    cropYear={parseInt(cropYear) || new Date().getFullYear()}
                />
            )}

            {selectedEditModal && (
                <EditCropProfileModal
                    isOpen={true}
                    onClose={() => setSelectedEditModal(null)}
                    customerId={customerId}
                    commodityType={selectedEditModal.commodityType}
                    cropYear={parseInt(cropYear) || new Date().getFullYear()}
                />
            )}
        </div>
    );
}


export function CustomerUnknownCropYearView({ baseTopLevelResult, customerId }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string }) {
    const [enableDelayedQuery, setEnableDelayedQuery] = useState(false);
    const unknownCropYearUnknownDataResult = useListRiskSummaryUnknown({ filters: { crop_year: 0, customer_ids: [customerId] }, enabled: enableDelayedQuery });

    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const unknownCropYearUnknownData = unknownCropYearUnknownDataResult?.data?.data?.summary || {};

    useEffect(() => {
        const timer = setTimeout(() => {
            setEnableDelayedQuery(true);
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    const { isReady } = useMemo(() => {
        if (!summaryData?.commodity_list || !summaryData?.customer_name_map) return { isReady: false };
        return { isReady: true };
    }, [summaryData]);

    const { isReady: isUnknownDataReady, commodity_top_level_items, unexpected_commodity_top_level_items } = useMemo(() => {
        if (!unknownCropYearUnknownData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
        if (!unknownCropYearUnknownData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

        const commodity_top_level_items = unknownCropYearUnknownData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ skipProfileCheck: true, commodity_type, customer_id: customerId, summaryData: unknownCropYearUnknownData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));
        const unexpected_commodity_top_level_items = commodity_top_level_items.filter((item: any) => item.commodity_type === item.commodity_display_name).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));
        const other = commodity_top_level_items.filter((item: any) => item.commodity_type !== item.commodity_display_name).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

        return { isReady: true, commodity_top_level_items: other, unexpected_commodity_top_level_items };
    }, [unknownCropYearUnknownData]);


    if (!isReady || !isUnknownDataReady) {
        return (
            <div className="flex flex-col gap-4">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
            </div>
        )
    }

    if (commodity_top_level_items.length === 0 && unexpected_commodity_top_level_items.length === 0) {
        return (
            <div className="border rounded">
                <p className="cursor-pointer px-4 py-2 font-semibold text-lg bg-gray-50 hover:bg-gray-100">
                    No Unknowns (Missing Crop Year)
                </p>
            </div>
        )
    }

    return (
        <details className="border rounded" open>
            <summary className="cursor-pointer px-4 py-2 font-semibold text-lg bg-gray-50 hover:bg-gray-100">
                Unknown Crop Year
            </summary>
            <div className="p-2">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4">
                        <h3 className="font-bold text-lg mb-2">Expected Commodities</h3>
                        <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left font-semibold text-primary">Commodity</th>
                                    <th className="px-4 py-2 text-left font-semibold text-blue-800">Futures Risk</th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-800">Physical Risk</th>
                                    <th className="px-4 py-2 text-left font-semibold text-green-800">DP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commodity_top_level_items.map((item: any) => (
                                    <tr
                                        key={`customer-unknown-crop-year-risk-${customerId}-${item.commodity_type}`}
                                        className="border-t"
                                    >
                                        {/* Commodity Name */}
                                        <td className="px-4 py-2 align-top font-semibold text-base text-primary whitespace-nowrap">
                                            {item.commodity_display_name}
                                        </td>
                                        {/* Futures Risk */}
                                        <td className="px-4 py-2 align-top">
                                            <div className="flex flex-wrap gap-2 text-xs lg:grid lg:grid-cols-1">
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-orange-400 pl-2">
                                                    <span className="font-semibold text-orange-700">OTC Total</span>
                                                    <span className="flex items-baseline gap-1">
                                                        <span className="font-mono text-orange-900">{formatNumberToDisplay(item.otc_full_quantity)}</span>
                                                    </span>
                                                </div>
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-blue-400 pl-2">
                                                    <span className="font-semibold text-blue-700">Open Brokerage</span>
                                                    <span className="font-mono text-blue-900">{formatNumberToDisplay(item.open_brokerage_quantity)}</span>
                                                </div>
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-pink-400 pl-2">
                                                    <span className="font-semibold text-pink-700">HTA</span>
                                                    <span className="font-mono text-pink-900">{formatNumberToDisplay(item.hta_quantity)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        {/* Physical Risk */}
                                        <td className="px-4 py-2 align-top">
                                            <div className="flex flex-wrap gap-2 text-xs">
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-cyan-400 pl-2">
                                                    <span className="font-semibold text-cyan-700">Basis</span>
                                                    <span className="font-mono text-cyan-900">{formatNumberToDisplay(item.basis_quantity)}</span>
                                                </div>
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-gray-400 pl-2">
                                                    <span className="font-semibold text-gray-700">Cash</span>
                                                    <span className="font-mono text-gray-900">{formatNumberToDisplay(item.cash_quantity)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        {/* DP Risk */}
                                        <td className="px-4 py-2 align-top">
                                            <div className="flex flex-wrap gap-2 text-xs">
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-green-400 pl-2">
                                                    <span className="font-semibold text-green-700">Applicable DP</span>
                                                    <span className="font-mono text-green-900">{formatNumberToDisplay(item.applicable_dp_quantity)}</span>
                                                </div>
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-lime-400 pl-2">
                                                    <span className="font-semibold text-lime-700">Non-App DP</span>
                                                    <span className="font-mono text-lime-900">{formatNumberToDisplay(item.non_applicable_dp_quantity)}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Unexpected Commodities / Positions Table */}
                    <div className="mt-6">
                        <h3 className="font-bold text-lg mb-2">Unexpected Commodities</h3>
                        <table className="min-w-full border text-xs">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="px-2 py-1 text-left">Commodity</th>
                                    <th className="px-2 py-1 text-left">Type</th>
                                    <th className="px-2 py-1 text-left">Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const unknownStats = unexpected_commodity_top_level_items;
                                    if (!Array.isArray(unknownStats) || unknownStats.length === 0) return null;

                                    // Each customerStats should have a commodity_type or commodity_display_name
                                    let rows: { commodity: string, type: string, quantity: number }[] = [];
                                    unknownStats.forEach((customerStats: any) => {
                                        if (!customerStats) return;
                                        const commodity =
                                            customerStats.commodity_display_name ||
                                            customerStats.commodity_type ||
                                            "Unknown";
                                        [
                                            { key: "open_brokerage_quantity", label: "Open Brokerage" },
                                            { key: "closed_brokerage_quantity", label: "Closed Brokerage" },
                                            { key: "non_applicable_open_brokerage_quantity", label: "Open Brokerage (Non-Applicable)" },
                                            { key: "non_applicable_closed_brokerage_quantity", label: "Closed Brokerage (Non-Applicable)" },
                                            { key: "otc_full_quantity", label: "OTC" },
                                            { key: "hta_quantity", label: "HTA" },
                                            { key: "basis_quantity", label: "Basis" },
                                            { key: "cash_quantity", label: "Cash" },
                                            { key: "applicable_dp_quantity", label: "Applicable DP" },
                                            { key: "non_applicable_dp_quantity", label: "Non-Applicable DP" },
                                        ].forEach(({ key, label }) => {
                                            const value = customerStats[key];
                                            if (typeof value === "number" && value !== 0) {
                                                rows.push({ commodity, type: label, quantity: value });
                                            }
                                        });
                                    });

                                    if (rows.length === 0) {
                                        return (
                                            <tr>
                                                <td className="px-2 py-1 text-gray-500" colSpan={3}>
                                                    No unexpected positions or commodities with value.
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return rows.map((row, idx) => (
                                        <tr key={row.commodity + row.type + idx}>
                                            <td className="px-2 py-1">{row.commodity}</td>
                                            <td className="px-2 py-1">{row.type}</td>
                                            <td className="px-2 py-1 font-mono">{row.quantity}</td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </details>
    )
}

export function CustomerUnknownCurrentCropYearView({ baseTopLevelResult, customerId }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull>, customerId: string }) {
    const [enableDelayedQuery, setEnableDelayedQuery] = useState(false);
    const [crop_year] = useQueryParam("crop_year", "");
    const currentCropYearUnknownDataResult = useListRiskSummaryUnknown({ filters: { crop_year: parseInt(crop_year), customer_ids: [customerId] }, enabled: enableDelayedQuery });

    const summaryData = baseTopLevelResult?.data?.data?.summary || {};
    const currentCropYearUnknownData = currentCropYearUnknownDataResult?.data?.data?.summary || {};

    useEffect(() => {
        const timer = setTimeout(() => {
            setEnableDelayedQuery(true);
        }, 1800);
        return () => clearTimeout(timer);
    }, []);

    const { isReady } = useMemo(() => {
        if (!summaryData?.commodity_list || !summaryData?.customer_name_map) return { isReady: false };
        return { isReady: true };
    }, [summaryData]);

    const { isReady: isUnknownDataReady, commodity_top_level_items, unexpected_commodity_top_level_items } = useMemo(() => {
        if (!currentCropYearUnknownData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
        if (!currentCropYearUnknownData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

        const accepted_commodity_list = ["C", "S", "CNL", "MW", "WW", "KW"];

        const commodity_top_level_items = currentCropYearUnknownData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ skipProfileCheck: true, commodity_type, customer_id: customerId, summaryData: currentCropYearUnknownData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name)).filter(
            (item: any) => !accepted_commodity_list.includes(item.commodity_type)
        );
        const unexpected_commodity_top_level_items = commodity_top_level_items.filter((item: any) => item.commodity_type === item.commodity_display_name).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));
        const other = commodity_top_level_items.filter((item: any) => item.commodity_type !== item.commodity_display_name).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

        return { isReady: true, commodity_top_level_items: other, unexpected_commodity_top_level_items };
    }, [currentCropYearUnknownData]);

    if (!isReady || !isUnknownDataReady) {
        return (
            <div className="flex flex-col gap-4">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
            </div>
        )
    }

    if (commodity_top_level_items.length === 0 && unexpected_commodity_top_level_items.length === 0) {
        return (
            <div className="border rounded">
                <p className="cursor-pointer px-4 py-2 font-semibold text-lg bg-gray-50 hover:bg-gray-100">
                    No Unknowns for {crop_year}
                </p>
            </div>
        )
    }

    return (
        <details className="border rounded" open>
            <summary className="cursor-pointer px-4 py-2 font-semibold text-lg bg-gray-50 hover:bg-gray-100">
                Unknowns (Current Crop Year)
            </summary>
            <div className="p-2">
                <h3 className="font-bold text-lg mb-2">Expected Commodities</h3>

                <div className="flex flex-col gap-4">

                    {commodity_top_level_items.length > 0 && (
                        <div className="flex flex-col gap-4">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-primary">Commodity</th>
                                        <th className="px-4 py-2 text-left font-semibold text-blue-800">Futures Risk</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-800">Physical Risk</th>
                                        <th className="px-4 py-2 text-left font-semibold text-green-800">DP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {commodity_top_level_items.map((item: any) => (
                                        <tr
                                            key={`customer-unknown-current-crop-year-risk-${customerId}-${item.commodity_type}`}
                                            className="border-t"
                                        >
                                            {/* Commodity Name */}
                                            <td className="px-4 py-2 align-top font-semibold text-base text-primary whitespace-nowrap">
                                                {item.commodity_display_name}
                                            </td>
                                            {/* Futures Risk */}
                                            <td className="px-4 py-2 align-top">
                                                <div className="flex flex-wrap gap-2 text-xs lg:grid lg:grid-cols-1">
                                                    <div className="flex flex-col items-start min-w-[80px] border-l-4 border-yellow-400 pl-2">
                                                        <span className="font-semibold text-yellow-700">Unknown OTC</span>
                                                        <span className="flex items-baseline gap-1">
                                                            <span className="font-mono text-yellow-900">{formatNumberToDisplay(item.unknown_otc_quantity)}</span>
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col items-start min-w-[80px] border-l-4 border-orange-400 pl-2">
                                                        <span className="font-semibold text-orange-700">OTC Total</span>
                                                        <span className="flex items-baseline gap-1">
                                                            <span className="font-mono text-orange-900">{formatNumberToDisplay(item.otc_full_quantity)}</span>
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col items-start min-w-[80px] border-l-4 border-blue-400 pl-2">
                                                        <span className="font-semibold text-blue-700">Open Brokerage</span>
                                                        <span className="font-mono text-blue-900">{formatNumberToDisplay(item.open_brokerage_quantity)}</span>
                                                    </div>
                                                    <div className="flex flex-col items-start min-w-[80px] border-l-4 border-pink-400 pl-2">
                                                        <span className="font-semibold text-pink-700">HTA</span>
                                                        <span className="font-mono text-pink-900">{formatNumberToDisplay(item.hta_quantity)}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Physical Risk */}
                                            <td className="px-4 py-2 align-top">
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-gray-400 pl-2 text-xs">
                                                    <span className="font-semibold text-gray-700">Cash</span>
                                                    <span className="font-mono text-gray-900">{formatNumberToDisplay(item.cash_quantity)}</span>
                                                </div>
                                            </td>
                                            {/* DP */}
                                            <td className="px-4 py-2 align-top">
                                                <div className="flex flex-col items-start min-w-[80px] border-l-4 border-green-400 pl-2 text-xs">
                                                    <span className="font-semibold text-green-700">DP</span>
                                                    <span className="font-mono text-green-900">{formatNumberToDisplay(item.applicable_dp_quantity)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>)}
                    <div className="mt-6">
                        <h3 className="font-bold text-lg mb-2">Unexpected Commodities</h3>
                        {unexpected_commodity_top_level_items.length > 0 && (
                            <table className="min-w-full border text-xs">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="px-2 py-1 text-left">Commodity</th>
                                        <th className="px-2 py-1 text-left">Type</th>
                                        <th className="px-2 py-1 text-left">Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const unknownStats = unexpected_commodity_top_level_items;
                                        if (!Array.isArray(unknownStats) || unknownStats.length === 0) return null;

                                        // Each customerStats should have a commodity_type or commodity_display_name
                                        let rows: { commodity: string, type: string, quantity: number }[] = [];
                                        unknownStats.forEach((customerStats: any) => {
                                            if (!customerStats) return;
                                            const commodity =
                                                customerStats.commodity_display_name ||
                                                customerStats.commodity_type ||
                                                "Unknown";
                                            [
                                                { key: "open_brokerage_quantity", label: "Open Brokerage" },
                                                { key: "closed_brokerage_quantity", label: "Closed Brokerage" },
                                                { key: "non_applicable_open_brokerage_quantity", label: "Open Brokerage (Non-Applicable)" },
                                                { key: "non_applicable_closed_brokerage_quantity", label: "Closed Brokerage (Non-Applicable)" },
                                                { key: "otc_full_quantity", label: "OTC" },
                                                { key: "hta_quantity", label: "HTA" },
                                                { key: "basis_quantity", label: "Basis" },
                                                { key: "cash_quantity", label: "Cash" },
                                                { key: "applicable_dp_quantity", label: "Applicable DP" },
                                                { key: "non_applicable_dp_quantity", label: "Non-Applicable DP" },
                                            ].forEach(({ key, label }) => {
                                                const value = customerStats[key];
                                                if (typeof value === "number" && value !== 0) {
                                                    rows.push({ commodity, type: label, quantity: value });
                                                }
                                            });
                                        });

                                        if (rows.length === 0) {
                                            return (
                                                <tr>
                                                    <td className="px-2 py-1 text-gray-500" colSpan={3}>
                                                        No unexpected positions or commodities with value.
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return rows.map((row, idx) => (
                                            <tr key={row.commodity + row.type + idx}>
                                                <td className="px-2 py-1">{row.commodity}</td>
                                                <td className="px-2 py-1">{row.type}</td>
                                                <td className="px-2 py-1 font-mono">{row.quantity}</td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </details>
    )
}
